/**
 * String utility functions for text manipulation, formatting, and validation
 */

import * as Api from '@ViewModels';

/**
 * Truncates text to a specified length with optional max line count
 * @source Original location: UiUtils.ts.backup line 890
 */

export const truncateTextToLength = (text: string, length: number, maxLineCount = 1000) => {
	let truncatedText = text || '';
	const lines = truncatedText
		.split('\n')
		.filter(x => !!x)
		.slice(0, maxLineCount);
	truncatedText = lines.join('\n');
	const len = Math.max(0, length);
	if (truncatedText.length > len) {
		truncatedText = truncatedText.substring(0, len);
		const lastWhiteSpaceIndex = truncatedText.search(/[\s ]$/);
		if (lastWhiteSpaceIndex >= 0) {
			truncatedText = truncatedText.substring(0, lastWhiteSpaceIndex);
		}
	}
	return truncatedText.trim();
};

/**
 * Converts HTML string to plain text
 * @source Original location: UiUtils.ts.backup line 908
 */
export const convertHtmlStringValueToPlainText = (htmlStringValue: string) => {
	const el = document.createElement('div');
	el.innerHTML = htmlStringValue;
	const text = el.textContent || '';
	if (el.parentNode) {
		el.parentNode.removeChild(el);
	}
	return text;
};

/**
 * Compares two strings for equality (case-insensitive)
 *  * @source Original location: UiUtils.ts.backup line 943
 */

export const stringsEqual = (
	firstString?: string,
	otherString?: string,
	options?: { caseInsensitiveCompare?: boolean }
) => {
	if (!!firstString && !!otherString) {
		if (!!options && !!options.caseInsensitiveCompare) {
			const lowerThis = firstString.toLocaleLowerCase();
			const lowerOtherString = otherString.toLocaleLowerCase();
			return lowerThis === lowerOtherString;
		}
	}

	if (firstString === otherString) {
		return true;
	}

	return false;
};

/**
 * Shortens a string to a maximum length with a separator
 * @source Original location: UiUtils.ts.backup line 976
 */
export const getShortenedString = (str: string, maxLen: number, separator = ' ') => {
	if (str?.length <= maxLen && !!str) {
		return str;
	}
	return str?.substring(0, str.lastIndexOf(separator, maxLen));
};

/**
 * Gets the last component of a path
 * @source Original location: UiUtils.ts.backup line 1004
 */
export const getLastPathComponent = (path: string) => {
	const pathComps = (path ? path : '').split('/').filter(x => !!x);
	const lastPathComp = pathComps.length > 0 ? pathComps[pathComps.length - 1] : '';
	return lastPathComp;
};

/**
 * Replaces the last component of a path
 * @source Original location: UiUtils.ts.backup line 1010
 */
export const replaceLastPathComponent = (path: string, replacement?: string) => {
	const pathComps = (path || '').split('/').filter(x => !!x);
	pathComps.splice(pathComps.length - 1, 1, replacement ? replacement : undefined);
	const result = pathComps.join('/');
	return result.charAt(0) === '/' ? result : `/${result}`;
};

/**
 * Gets the industry name by removing underscores
 * @source Original location: UiUtils.ts.backup line 1240
 */
export const getIndustryName = (industry: Api.Industry | 'All') => {
	return industry.replace(/_/gi, '');
};

/**
 * Gets the discount name from account discount percent
 * @source Original location: UiUtils.ts.backup line 963
 */
export const getDiscountName = (discount: Api.AccountDiscountPercent) => {
	switch (discount) {
		case Api.AccountDiscountPercent.Ten:
			return '10%';
		case Api.AccountDiscountPercent.Fifteen:
			return '15%';
		case Api.AccountDiscountPercent.Twenty:
			return '20%';
		case Api.AccountDiscountPercent.TwentyFive:
			return '25%';
		case Api.AccountDiscountPercent.Thirty:
			return '30%';
		case Api.AccountDiscountPercent.ThirtyFive:
			return '35%';
		case Api.AccountDiscountPercent.Forty:
			return '40%';
		case Api.AccountDiscountPercent.Fifty:
			return '50%';
		default:
			return '';
	}
};
